import { FindhelpProgram, FindhelpOffice, FindhelpCategory, UniteUsService } from '@src/api.types';
import { UniteUsOffice } from '../../../../packages/ui/src/CboCardUniteUs/CboCardUniteUs';
import { parentCategory } from '@src/routes/Search/Search';
// SearchInputOption removed in V2 - define locally if needed
export type SearchInputOption = { value: string; label: string; [key: string]: any };
import { API } from '@src/api';
import { useState } from 'react';
import {
  UniteUsOfficeLocations,
  UniteUsProgram,
  UniteUsOfficeHours,
} from '../../../../packages/ui/src';
export type DateObject = {
  month: number;
  day: number;
  year: number;
};
export type Day =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

/**
 * Given a date of birth as a string formatted "mm/dd/YYYY",
 *  return the date as an object in the form DateObject so that
 *  it is easier to format the date into other presentations
 *  (for example, dates separated by a "/" or "-",
 *  month first versus year first, etc).
 */
export function dobStringToObject(dob: string): DateObject | undefined {
  const split = dob.split('/');

  try {
    const result = {
      month: parseInt(split[0], 10),
      day: parseInt(split[1], 10),
      year: parseInt(split[2], 10),
    };
    return result;
  } catch (error: any) {
    console.error(
      `Could not parse dob ${dob} into date object, error was `,
      error
    );
  }

  return undefined;
}
export function getOpenHoursText(office: FindhelpOffice, day: Day): string {
  const openHours = office?.hours;

  // Attribute `office.monday` is a boolean indicating closed or open on that day
  return openHours[`${day}`]
    ? // If the office is open on the given day, return a string of open & close times
      `${openHours[`${day}_start`]} - ${openHours[`${day}_finish`]}`
    : // Otherwise return 'Closed'
      'Closed';
}


export function getOpenHoursTextUniteUs(hours: Record<string, { opens: string; closes: string }[]>, day: string): string {
  if (!hours) {
    return 'Closed'; // If no hours are provided, return 'Closed'
  }
  const dayHours = hours[day.toLowerCase()]; // Get the hours for the given day

  // Check if the office is open on the given day
  if (dayHours && dayHours.length > 0) {
    // Return the first open-close time range (assuming one range per day)
    return `${dayHours[0].opens} - ${dayHours[0].closes}`;
  }

  // If no hours are available for the given day, return 'Closed'
  return 'Closed';
}

export function getOpenHoursTextForUniteus(
  office: UniteUsOfficeLocations,
  day: keyof UniteUsOfficeHours
): string {
  const openHours = office?.hours;

  if (
    !openHours ||
    !Array.isArray(openHours[day]) ||
    openHours[day].length === 0
  ) {
    return 'Closed';
  }

  // Access the first opening period for the given day
  const dayHours = openHours[day][0];
  return `${dayHours.opens} - ${dayHours.closes}`;
}


export function getAddressText(programData: FindhelpProgram): string {
  const office: FindhelpOffice | undefined = programData?.offices[0];

  const addr2text = office?.address2 ? ` ${office?.address2}` : '';

  return office
    ? `${office?.address1}${addr2text}, ${office?.city}, ${office?.state} ${office?.postal}`
    : '--';
}



export function getAddressTextUniteUs(office: UniteUsOffice | undefined): string {
  if (!office) {
    return '--';
  }

  const parts = [
    office.line_1,
    office.line_2,
    office.city,
    office.state,
    office.postal_code,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : '--';
}

export type FormattedDate = `${number}/${number}/${number}`;

export function getDOBInMonthDayYearFormat(
  dob: string | undefined
): string | undefined {
  try {
    // for java (as dob in string format)
    if (dob) {
      if (dob.includes('/')) {
        const a = dob.split('/');
        return `${a[0]}/${a[1]}/${a[2]}`;
      }
      if (dob.includes('-')) {
        const a = dob.split('-');
        return `${a[1]}/${a[2]}/${a[0]}`;
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function storeToArrayWellSky(
  array: any[],
  output: any[],
  level: number,
  parent: string,
  alevel: number,
  pid: number
) {
  for (let i = 0; i < array.length; i++) {
    if (level == 1)
      output.push({
        id: array[i].id,
        label: array[i].label,
        level,
        parent: '',
        alevel: 1,
        pid: array[i].id,
        external_id: array[i].external_id,
      });
    else {
      const tp = `${parent}`;
      const parentS = null;
      const paray: any[] = [];
      calculateParentWellSky(paray, alevel, output, tp, parentS, pid);
      const c = {
        id: array[i].id,
        label: array[i].label,
        level,
        alevel,
        parent,
        paray,
        pid,
        external_id: array[i].external_id,
      };
      output.push(c);
    }

    if (array[i].children) {
      storeToArrayWellSky(
        array[i].children,
        output,
        2,
        array[i].label,
        alevel + 1,
        array[i].id
      );
    }
  }
}

export function calculateParentWellSky(
  paray: parentCategory[],
  alevel: number,
  output: any,
  tp: string,
  parentS: any,
  tpid: number
) {
  if (alevel > 2) {
    for (let i = alevel; i > 1; i--) {
      parentS = output.filter((ct) => {
        return ct.label == tp && ct.id == tpid;
      });
      if (parentS.length > 0 && parentS[0]?.parent) {
        tp = parentS[0].parent;
        tpid = parentS[0].pid;
      }
      if (parentS.length > 0)
        paray.push({ label: parentS[0].label, id: parentS[0].id });
    }
  }
  paray.reverse();
}

// Given a list of elements which may have recursive children elements,
// find the element with the given label using a recursive search.
type TreeNode = {
  label: string;
  children?: TreeNode[];
};

export function findRecursive(
  label: string,
  elements: TreeNode[]
): TreeNode | undefined {
  for (const element of elements) {
    if (element.label === label) {
      return element;
    }

    if (element.children) {
      const found = findRecursive(label, element.children);
      if (found) {
        return found;
      }
    }
  }
}



export function findRecursivePathv2(
   label: string,
   elements: TreeNode[],
   path: TreeNode[] = []
  ): TreeNode[] | undefined {
   for (const element of elements) {
   const currentPath = [...path, element];

   if (element.label.toLocaleLowerCase() === label.toLocaleLowerCase()) {
   return currentPath;
   }

   if (element.children) {
   const result = findRecursivePathv2(label, element.children, currentPath);
   if (result) {
   return result;
   }
   }
   }

   return undefined;
  }



